import { useState } from 'react';
import type { AxiosError } from 'axios';
import { mergeSceneNarration } from '../../../features/scenarios/api';
import { useToast } from '../../../hooks/useToast';
import { Tooltip } from '../../common/Tooltip';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import styles from './ScenarioGenerator.module.css';

/** 動画とナレーション音声を合成するボタン */
export function MergeNarrationButton({ sceneId, sceneOrder, onMerged, videoHasAudio }: { sceneId: string; sceneKey: string; sceneOrder: number; onMerged?: (materialId: string, durationSeconds?: number) => void; videoHasAudio?: boolean }) {
  const [merging, setMerging] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const toast = useToast();

  const handleMerge = async () => {
    setConfirmOpen(false);
    setMerging(true);
    try {
      const data = await mergeSceneNarration(sceneId);
      toast.success(`シーン${sceneOrder}の動画にナレーションを合成しました`);
      const mat = data?.material;
      if (mat && onMerged) {
        onMerged(mat.id, mat.duration_seconds);
      }
    } catch (err) {
      const axiosErr = err as AxiosError<{ error?: string }>;
      const msg = axiosErr.response?.data?.error || '合成に失敗しました';
      toast.error(msg);
    } finally {
      setMerging(false);
    }
  };

  if (videoHasAudio) {
    return (
      <Tooltip content="この機能は動画が音声なしの場合のみ使えます">
        <button
          type="button"
          className={styles.mergeNarrationButton}
          disabled
          style={{ opacity: 0.4, cursor: 'default' }}
        >
          <span>動画に合成</span>
        </button>
      </Tooltip>
    );
  }

  return (
    <>
      <button
        type="button"
        className={styles.mergeNarrationButton}
        onClick={() => setConfirmOpen(true)}
        disabled={merging}
        title="動画にナレーション音声を合成"
      >
        {merging && <span className={styles.spinnerSmall} />}
        <span>{merging ? '合成中...' : '動画に合成'}</span>
      </button>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="動画にナレーションを合成"
        message="この機能を利用して動画とナレーションを合成すると、音声のタイミングなどが適切でない場合があります。高品質な動画素材を生成する場合は、動画編集ソフトでの合成を推奨いたします。"
        confirmLabel="合成する"
        cancelLabel="キャンセル"
        variant="info"
        onConfirm={handleMerge}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
